import { useLocalStorageValue } from "@react-hookz/web";
import { dataObjectsDeepEqual } from "@smp/utils";
import endOfWeek from "date-fns/endOfWeek";
import startOfWeek from "date-fns/startOfWeek";
import produce from "immer";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";
import { useUpdateEffect } from "react-use";

import { filtersFeedInitial } from "./posts.static";
import { IContextPosts, IContextPostsScheduleDates } from "./posts.types";

export const STORAGE_KEY__INSTA_VIEW_PAGE =
  "easypostUserStorage.INSTA_VIEW_PAGE";
export const STORAGE_KEY__INSTA_VIEW_GROUP =
  "easypostUserStorage.INSTA_VIEW_GROUP";
export const STORAGE_KEY__WEEK_VIEW_GROUP =
  "easypostUserStorage.WEEK_VIEW_GROUP";

const ContextPosts = createContext<IContextPosts>(
  null as unknown as IContextPosts,
);

export const ProviderPosts = ({
  children,
  onLoad,
}: PropsWithChildren<{
  onLoad?: () => void;
}>) => {
  const [storageInstaPageConfigId, setStorageInstaPageConfigId] =
    useLocalStorageValue<number>(STORAGE_KEY__INSTA_VIEW_PAGE, 0);

  const [storageInstaSocialPageGroupId, setStorageInstaSocialPageGroupId] =
    useLocalStorageValue<number>(STORAGE_KEY__INSTA_VIEW_GROUP, 0);

  const [storageWeekSocialPageGroupId, setStorageWeekSocialPageGroupId] =
    useLocalStorageValue<number>(STORAGE_KEY__WEEK_VIEW_GROUP, 0);

  const startOfWeekInitial = startOfWeek(new Date(), {
    weekStartsOn: 1,
  }).toISOString();

  const endOfWeekInitial = endOfWeek(new Date(), {
    weekStartsOn: 1,
  }).toISOString();

  const filtersWeekFromStorage = {
    areSlotsInMixedOrder: false,
    scheduledFor: { gte: startOfWeekInitial, lte: endOfWeekInitial },
    socialPageGroupId: storageWeekSocialPageGroupId || null,
  };

  const filtersInstaInitialFromStorage = {
    isPreviewMode: false,
    pageConfigId: storageInstaPageConfigId || null,
    socialPageGroupId: storageInstaSocialPageGroupId || null,
  };

  const [scheduleButtonDateList, setScheduleButtonDateList] =
    useState<IContextPostsScheduleDates>({});

  const [filtersFeed, setFiltersFeed] =
    useState<IContextPosts["state"]["filtersFeed"]>(filtersFeedInitial);

  const [filtersInsta, setFiltersInsta] = useState<
    IContextPosts["state"]["filtersInsta"]
  >(filtersInstaInitialFromStorage);

  const [filtersWeek, setFiltersWeek] = useState<
    IContextPosts["state"]["filtersWeek"]
  >(filtersWeekFromStorage);

  const [filtersCalendarKey, setFiltersCalendarKey] = useState(0);

  const changeFeedFilters: IContextPosts["actions"]["changeFeedFilters"] = (
    filters: IContextPosts["state"]["filtersFeed"],
  ) => {
    setFiltersFeed(filters);
  };

  const changeInstaFilters: IContextPosts["actions"]["changeInstaFilters"] = (
    filters: IContextPosts["state"]["filtersInsta"],
  ) => {
    setFiltersInsta(filters);
    filters.pageConfigId && setStorageInstaPageConfigId(filters.pageConfigId);
    filters.socialPageGroupId &&
      setStorageInstaSocialPageGroupId(filters.socialPageGroupId);
  };

  const changeWeekFilters: IContextPosts["actions"]["changeWeekFilters"] = (
    filters: IContextPosts["state"]["filtersWeek"],
  ) => {
    setFiltersWeek(filters);
    filters.socialPageGroupId &&
      setStorageWeekSocialPageGroupId(filters.socialPageGroupId);

    if (filters.socialPageGroupId) {
      localStorage.setItem(
        STORAGE_KEY__WEEK_VIEW_GROUP,
        filters.socialPageGroupId.toString(),
      );
    } else {
      localStorage.removeItem(STORAGE_KEY__WEEK_VIEW_GROUP);
    }
  };

  const addScheduleButtonDates: IContextPosts["actions"]["addScheduleButtonDates"] =
    useCallback((weekDay: number, dates: string[]) => {
      setScheduleButtonDateList((state) =>
        produce(state, (draft) => {
          draft[weekDay] = dates;
        }),
      );
    }, []);

  const resetFeedFilters = () => {
    setFiltersCalendarKey((state) => state + 1);
    setFiltersFeed(filtersFeedInitial);
  };

  const resetInstaFilters = () =>
    setFiltersInsta(filtersInstaInitialFromStorage);

  const toggleIsInstaPreviewMode = () =>
    setFiltersInsta(
      produce(filtersInsta, (draft) => {
        draft.isPreviewMode = !draft.isPreviewMode;
      }),
    );

  const areFeedFiltersDirty = !dataObjectsDeepEqual(
    filtersFeedInitial,
    filtersFeed,
  );

  const areInstaFiltersDirty = !dataObjectsDeepEqual(
    filtersInstaInitialFromStorage,
    filtersInsta,
  );

  const areWeekFiltersDirty = !dataObjectsDeepEqual(
    filtersWeekFromStorage,
    filtersWeek,
  );

  useUpdateEffect(() => {
    setFiltersWeek((state) => ({
      ...state,
      socialPageGroupId: storageWeekSocialPageGroupId,
    }));
  }, [storageWeekSocialPageGroupId]);

  useUpdateEffect(() => {
    setFiltersInsta((state) => ({
      ...state,
      socialPageGroupId: storageInstaSocialPageGroupId,
    }));
  }, [storageInstaSocialPageGroupId]);

  return (
    <ContextPosts.Provider
      value={{
        actions: {
          addScheduleButtonDates,
          changeFeedFilters,
          changeInstaFilters,
          changeWeekFilters,
          resetFeedFilters,
          resetInstaFilters,
          toggleIsInstaPreviewMode,
          onLoad,
        },
        state: {
          areFeedFiltersDirty,
          areInstaFiltersDirty,
          areWeekFiltersDirty,
          filtersFeed,
          filtersInsta,
          filtersWeek,
          filtersCalendarKey,
          scheduleButtonDateList,
        },
      }}
    >
      {children}
    </ContextPosts.Provider>
  );
};

export const useContextPosts = () => useContext(ContextPosts);
